'use client';

import { useActionState, useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createContactRequest, State } from '@/app/lib/contact-actions';
import { ArrowRight, ArrowLeft, Send, Loader2, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

const ease = [0.22, 1, 0.36, 1] as const;
const SOURCE_COOKIE_NAME = 'refineed_source';
const TOTAL_STEPS = 5;

type FormValues = {
  companyName: string;
  vatNumber: string;
  name: string;
  email: string;
  phone: string;
  goal: string;
  privacyAccepted: boolean;
};

const empty: FormValues = {
  companyName: '',
  vatNumber: '',
  name: '',
  email: '',
  phone: '',
  goal: '',
  privacyAccepted: false,
};

function buildFallbackSourceData() {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || undefined;
  let sourceLabel = 'Diretto';
  if (params.get('utm_source')) {
    sourceLabel = [params.get('utm_source'), params.get('utm_medium'), params.get('utm_campaign')]
      .filter(Boolean)
      .join(' / ');
  } else if (referrer) {
    try { sourceLabel = new URL(referrer).hostname.replace(/^www\./, ''); } catch { sourceLabel = referrer; }
  }
  return {
    sourceLabel,
    landingPath: `${window.location.pathname}${window.location.search}`,
    landingUrl: window.location.href,
    referrer,
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmTerm: params.get('utm_term') || undefined,
    utmContent: params.get('utm_content') || undefined,
    capturedAt: new Date().toISOString(),
  };
}

// ── Shared input ──────────────────────────────────────────────────────────────

function UnderlineInput({
  type = 'text',
  value,
  onChange,
  onKeyDown,
  placeholder,
  autoFocus,
  uppercase,
}: {
  type?: string;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoFocus?: boolean;
  uppercase?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete="off"
      className="w-full bg-transparent border-0 border-b border-white/15 py-3 text-xl text-white placeholder:text-gray-700 focus:outline-none focus:border-primary transition-colors duration-200"
    />
  );
}

// ── Step components ───────────────────────────────────────────────────────────

function StepCompany({
  values, update, onKeyDown, error,
}: { values: FormValues; update: (f: keyof FormValues, v: string) => void; onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void; error: string | null }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Iniziamo</p>
      <h2 className="text-2xl font-bold text-white leading-snug mb-2">Come si chiama la tua azienda?</h2>
      <p className="text-sm text-gray-500 mb-8">Ragione sociale completa, poi la P.IVA</p>

      <div className="space-y-8">
        <UnderlineInput
          value={values.companyName}
          onChange={v => update('companyName', v)}
          onKeyDown={onKeyDown}
          placeholder="Es. Rossi & Figli S.r.l."
          autoFocus
        />
        <div>
          <div className="mb-2 text-xs text-gray-600 font-medium">...e la P.IVA?</div>
          <UnderlineInput
            value={values.vatNumber}
            onChange={v => update('vatNumber', v)}
            onKeyDown={onKeyDown}
            placeholder="IT01234567890"
            uppercase
          />
        </div>
      </div>

      {error && <p className="mt-5 text-sm text-red-400">{error}</p>}
      <p className="mt-6 text-xs text-gray-700">
        Premi{' '}
        <kbd className="rounded bg-white/[0.05] px-1.5 py-0.5 font-mono text-gray-500">Enter</kbd>{' '}
        per continuare
      </p>
    </div>
  );
}

function StepContact({
  values, update, onKeyDown, error,
}: { values: FormValues; update: (f: keyof FormValues, v: string) => void; onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void; error: string | null }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Tu</p>
      <h2 className="text-2xl font-bold text-white leading-snug mb-2">Chi sei, e come ti contatto?</h2>
      <p className="text-sm text-gray-500 mb-8">Nome e indirizzo email. Nulla di più.</p>

      <div className="space-y-8">
        <UnderlineInput
          value={values.name}
          onChange={v => update('name', v)}
          onKeyDown={onKeyDown}
          placeholder="Nome e cognome"
          autoFocus
        />
        <div>
          <div className="mb-2 text-xs text-gray-600 font-medium">...e la tua email?</div>
          <UnderlineInput
            type="email"
            value={values.email}
            onChange={v => update('email', v)}
            onKeyDown={onKeyDown}
            placeholder="nome@azienda.it"
          />
        </div>
      </div>

      {error && <p className="mt-5 text-sm text-red-400">{error}</p>}
      <p className="mt-6 text-xs text-gray-700">
        Premi{' '}
        <kbd className="rounded bg-white/[0.05] px-1.5 py-0.5 font-mono text-gray-500">Enter</kbd>{' '}
        per continuare
      </p>
    </div>
  );
}

function StepPhone({
  values, update, onKeyDown,
}: { values: FormValues; update: (f: keyof FormValues, v: string) => void; onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Facoltativo</p>
      <h2 className="text-2xl font-bold text-white leading-snug mb-2">Un numero di telefono?</h2>
      <p className="text-sm text-gray-500 mb-8">
        Solo se vuoi che ti richiamiamo. Altrimenti puoi saltare.
      </p>

      <UnderlineInput
        type="tel"
        value={values.phone}
        onChange={v => update('phone', v)}
        onKeyDown={onKeyDown}
        placeholder="+39 333 1234567"
        autoFocus
      />

      <p className="mt-6 text-xs text-gray-700">
        Premi{' '}
        <kbd className="rounded bg-white/[0.05] px-1.5 py-0.5 font-mono text-gray-500">Enter</kbd>{' '}
        per continuare o <span className="text-gray-500">Salta</span> per procedere senza
      </p>
    </div>
  );
}

function StepGoal({
  values, update, error,
}: { values: FormValues; update: (f: keyof FormValues, v: string) => void; error: string | null }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Quasi finito</p>
      <h2 className="text-2xl font-bold text-white leading-snug mb-2">Cosa vorresti realizzare?</h2>
      <p className="text-sm text-gray-500 mb-8">
        Poche righe bastano. Il team risponde con un'analisi concreta sulla tua esigenza.
      </p>

      <textarea
        value={values.goal}
        onChange={e => update('goal', e.target.value)}
        rows={5}
        autoFocus
        placeholder="Es. Vogliamo automatizzare i preventivi, creare un CRM su misura o rifare completamente il nostro sito..."
        className="w-full resize-none bg-transparent border-0 border-b border-white/15 py-3 text-base text-white placeholder:text-gray-700 focus:outline-none focus:border-primary transition-colors duration-200 leading-7"
      />

      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
    </div>
  );
}

function StepConfirm({
  values,
  update,
  error,
  serverMessage,
  isPending,
}: {
  values: FormValues;
  update: (f: keyof FormValues, v: boolean) => void;
  error: string | null;
  serverMessage: string | null | undefined;
  isPending: boolean;
}) {
  const rows = [
    { label: 'Azienda', value: values.companyName },
    { label: 'P.IVA', value: values.vatNumber },
    { label: 'Referente', value: values.name },
    { label: 'Email', value: values.email },
    ...(values.phone ? [{ label: 'Telefono', value: values.phone }] : []),
  ];

  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Ci siamo</p>
      <h2 className="text-2xl font-bold text-white leading-snug mb-2">Invia la tua richiesta.</h2>
      <p className="text-sm text-gray-500 mb-6">Controlla i dati e poi spedisci. Il team risponde entro 3 ore.</p>

      <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] divide-y divide-white/[0.05] mb-6">
        {rows.map(row => (
          <div key={row.label} className="flex items-center justify-between px-4 py-3">
            <span className="text-xs text-gray-600 w-20 shrink-0">{row.label}</span>
            <span className="text-sm text-gray-200 text-right truncate">{row.value}</span>
          </div>
        ))}
        <div className="px-4 py-3">
          <span className="text-xs text-gray-600 block mb-1">Obiettivo</span>
          <span className="text-sm text-gray-300 leading-6 line-clamp-3">{values.goal}</span>
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <div
          role="checkbox"
          aria-checked={values.privacyAccepted}
          tabIndex={0}
          onClick={() => update('privacyAccepted', !values.privacyAccepted)}
          onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && update('privacyAccepted', !values.privacyAccepted)}
          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors duration-150 ${
            values.privacyAccepted
              ? 'border-primary bg-primary'
              : 'border-white/25 bg-transparent'
          }`}
        >
          {values.privacyAccepted && (
            <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 10" stroke="currentColor" strokeWidth={2}>
              <path d="M1.5 5l2.5 2.5 4.5-4.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <span className="text-sm text-gray-400 leading-relaxed">
          Acconsento al trattamento dei dati personali secondo la{' '}
          <Link href="/privacy-policy" className="text-primary hover:underline">
            privacy policy
          </Link>
          .
        </span>
      </label>

      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
      {serverMessage && !isPending && (
        <p className="mt-4 text-sm text-red-400">{serverMessage}</p>
      )}
    </div>
  );
}

// ── Main form ─────────────────────────────────────────────────────────────────

export default function ContactForm() {
  const initialState: State = { message: null, errors: {} };
  const [state, formAction, isPending] = useActionState(createContactRequest, initialState);
  const formRef = useRef<HTMLFormElement>(null);

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [sourceData, setSourceData] = useState('');
  const [values, setValues] = useState<FormValues>(empty);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find(r => r.startsWith(`${SOURCE_COOKIE_NAME}=`))
      ?.split('=')[1];
    if (cookieValue) {
      try { setSourceData(decodeURIComponent(cookieValue)); return; } catch {}
    }
    setSourceData(JSON.stringify(buildFallbackSourceData()));
  }, []);

  useEffect(() => {
    if (state.success) {
      setStep(0);
      setValues(empty);
      setFieldError(null);
    }
  }, [state.success]);

  useEffect(() => {
    if (!state.errors) return;
    if (state.errors.goal) { goTo(3); return; }
    if (state.errors.companyName || state.errors.vatNumber) { goTo(0); return; }
    if (state.errors.name || state.errors.email) { goTo(1); return; }
    if (state.errors.privacyAccepted) { goTo(4); return; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.errors]);

  function goTo(next: number) {
    setDirection(next > step ? 1 : -1);
    setFieldError(null);
    setStep(next);
  }

  function update(field: keyof FormValues, value: string | boolean) {
    setValues(v => ({ ...v, [field]: value }));
    setFieldError(null);
  }

  function validate(): boolean {
    if (step === 0) {
      if (!values.companyName.trim()) { setFieldError('Inserisci il nome della tua azienda'); return false; }
      if (!values.vatNumber.trim()) { setFieldError('Inserisci la P.IVA'); return false; }
    }
    if (step === 1) {
      if (!values.name.trim()) { setFieldError('Inserisci il tuo nome'); return false; }
      if (!values.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        setFieldError("Inserisci un'email valida");
        return false;
      }
    }
    if (step === 3) {
      if (values.goal.trim().length < 10) { setFieldError('Raccontaci qualcosa di più (min. 10 caratteri)'); return false; }
    }
    if (step === 4) {
      if (!values.privacyAccepted) { setFieldError('Devi accettare la privacy policy per procedere'); return false; }
    }
    return true;
  }

  function advance() {
    if (!validate()) return;
    if (step < TOTAL_STEPS - 1) goTo(step + 1);
  }

  function skip() {
    setFieldError(null);
    goTo(step + 1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      advance();
    }
  }

  function handleSubmit() {
    if (!validate()) return;
    formRef.current?.requestSubmit();
  }

  // ── Success screen ──
  if (state.success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="flex min-h-[480px] flex-col items-start justify-center"
      >
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 border border-green-500/20">
          <CheckCircle2 className="h-6 w-6 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white">Richiesta inviata.</h2>
        <p className="mt-3 max-w-sm text-base leading-7 text-gray-400">
          Il team analizzerà la tua richiesta e ti risponderà con un'analisi concreta.
        </p>
        <p className="mt-8 text-xs text-gray-700">
          Nel frattempo puoi scoprire{' '}
          <Link href="/#soluzioni" className="text-gray-500 hover:text-white transition-colors">
            come lavoriamo
          </Link>
          .
        </p>
      </motion.div>
    );
  }

  const progress = (step / (TOTAL_STEPS - 1)) * 100;

  return (
    <form ref={formRef} action={formAction} className="select-none">
      {/* Hidden form values */}
      <input type="hidden" name="sourceData" value={sourceData} />
      <input type="hidden" name="companyName" value={values.companyName} />
      <input type="hidden" name="vatNumber" value={values.vatNumber} />
      <input type="hidden" name="name" value={values.name} />
      <input type="hidden" name="email" value={values.email} />
      <input type="hidden" name="phone" value={values.phone} />
      <input type="hidden" name="goal" value={values.goal} />
      <input type="hidden" name="privacyAccepted" value={values.privacyAccepted ? 'accepted' : ''} />

      {/* Progress bar */}
      <div className="mb-8 h-px bg-white/[0.07] relative overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-primary"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease }}
        />
      </div>

      {/* Step counter + back */}
      <div className="mb-8 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-widest text-gray-700">
          {step + 1} / {TOTAL_STEPS}
        </span>
        {step > 0 && (
          <button
            type="button"
            onClick={() => goTo(step - 1)}
            className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Indietro
          </button>
        )}
      </div>

      {/* Step content with slide animation */}
      <div className="min-h-[340px]">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={{
              enter: (dir: number) => ({ x: dir * 48, opacity: 0 }),
              center: { x: 0, opacity: 1 },
              exit: (dir: number) => ({ x: dir * -48, opacity: 0 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.26, ease }}
          >
            {step === 0 && (
              <StepCompany
                values={values}
                update={(f, v) => update(f, v as string)}
                onKeyDown={handleKeyDown}
                error={fieldError}
              />
            )}
            {step === 1 && (
              <StepContact
                values={values}
                update={(f, v) => update(f, v as string)}
                onKeyDown={handleKeyDown}
                error={fieldError}
              />
            )}
            {step === 2 && (
              <StepPhone
                values={values}
                update={(f, v) => update(f, v as string)}
                onKeyDown={handleKeyDown}
              />
            )}
            {step === 3 && (
              <StepGoal
                values={values}
                update={(f, v) => update(f, v as string)}
                error={fieldError}
              />
            )}
            {step === 4 && (
              <StepConfirm
                values={values}
                update={(f, v) => update(f, v as boolean)}
                error={fieldError}
                serverMessage={state.message}
                isPending={isPending}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom navigation */}
      <div className="mt-8 flex items-center justify-between gap-4">
        <div>
          {step === 2 && (
            <button
              type="button"
              onClick={skip}
              className="text-sm text-gray-600 hover:text-gray-300 transition-colors animate-btn-pulse-delayed"
            >
              Salta
            </button>
          )}
        </div>

        {step < TOTAL_STEPS - 1 ? (
          <button
            type="button"
            onClick={advance}
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90 active:scale-[0.98] transition-all animate-btn-pulse"
          >
            Continua
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isPending}
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Invia la richiesta
              </>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
